// frontend/src/components/pages/AutoTradingDetailHistoryPage.js
import { formatExitReason } from '../../utils/exitReasonLabel';
import { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  sortTradesByExitTime,
  remainingAfterExit,
  closedTradeCycles,
} from '../../utils/autoTradingExits';

const EXIT_REASON_LABELS = {
  stop_loss: '손절',
  break_even: '본절 청산',
  take_profit_30: '30p 분할 익절',
  take_profit_45: '45p 분할 익절',
  power_50: '강도 50% 익절',
  power_zero: '강도 0 익절',
  market_end: '장마감 전 청산',
  risk_block: '손실 한도 초과',
  power_80: '강도 80% 익절',
  power_85: '강도 85% 익절',
  power_90: '강도 90% 익절',
  premarket_profit_over_100p: '프리장 100p 수익 익절',
  price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
};

function isBreakEvenExit(reason) {
  if (!reason) return false;
  // JSONB가 객체로 올 가능성 방어: {type: 'break_even', ...} 등
  if (typeof reason === 'object') {
    const code = reason.type || reason.reason || reason.code;
    return code === 'break_even';
  }
  return reason === 'break_even';
}

// 서머타임 여부 확인: true (CDT, Daylight Time(서머타임)), false (CST, Standard Time)
const IS_DST = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
})
  .formatToParts(new Date())
  .some((p) => p.value === 'CDT');

function mapExitReason(reason, event) {
  return formatExitReason(reason, event, EXIT_REASON_LABELS);
}

/** 공통: axios 취소 여부 판별 */
function isCanceled(err) {
  return (
    err?.code === 'ERR_CANCELED' ||
    err?.name === 'CanceledError' ||
    (typeof axios.isCancel === 'function' && axios.isCancel(err))
  );
}

/** KST 관련 Helper */
function toKstDate(d) {
  // Date -> "KST 시각과 동일한 현지 Date"로 변환 (표현/계산용)
  return new Date(
    new Date(d).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
  );
}

function ymdKst(d) {
  const k = toKstDate(d);
  const y = k.getFullYear();
  const m = String(k.getMonth() + 1).padStart(2, '0');
  const day = String(k.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysKst(ymd, delta) {
  const base = new Date(`${ymd}T00:00:00+09:00`);
  base.setDate(base.getDate() + delta);
  return ymdKst(base);
}

//** 숫자 표시 Helper */
function toFiniteNumber(value, fallback = null) {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function fmtPrice(value) {
  const n = toFiniteNumber(value);
  return n == null ? '' : n.toFixed(2);
}

function fmtQty(value) {
  const n = toFiniteNumber(value, 0);
  return Number.isFinite(n) ? String(Math.trunc(n)) : '0';
}

function pickFirstNumber(...values) {
  for (const v of values) {
    const n = toFiniteNumber(v);
    if (n != null) return n;
  }
  return null;
}

function normalizeFills(fills) {
  if (!Array.isArray(fills)) return [];
  return fills
    .map((f) => ({
      exec_no: f.exec_no ?? f.broker_exec_no ?? f.OvrsFutsExecNo ?? '',
      qty: toFiniteNumber(f.qty ?? f.fill_qty ?? f.ExecQty, 0),
      price: toFiniteNumber(f.price ?? f.fill_price ?? f.AbrdFutsExecPrc),
      t: f.t ?? f.fill_time ?? f.ExecDttm ?? null,
    }))
    .filter((f) => f.qty > 0 && f.price != null);
}

function weightedAvgFromFills(fills) {
  const normalized = normalizeFills(fills);
  const qty = normalized.reduce((acc, f) => acc + f.qty, 0);
  if (qty <= 0) return null;
  return normalized.reduce((acc, f) => acc + f.price * f.qty, 0) / qty;
}

function decisionEntryPriceFromFills(directionLong, fills) {
  const prices = normalizeFills(fills).map((f) => f.price);
  if (!prices.length) return null;
  return directionLong ? Math.min(...prices) : Math.max(...prices);
}

function eventTimeFromFillsOrEvent(e) {
  const fills = normalizeFills(e?.fills);
  const fillTimes = fills
    .map((f) => f.t)
    .filter(Boolean)
    .map((t) => new Date(t))
    .filter((d) => !Number.isNaN(d.getTime()));

  if (fillTimes.length > 0) {
    return new Date(
      Math.min(...fillTimes.map((d) => d.getTime())),
    ).toISOString();
  }

  return e?.t ?? null;
}

function exitTimeFromFillsOrEvent(e) {
  const fills = normalizeFills(e?.fills);
  const fillTimes = fills
    .map((f) => f.t)
    .filter(Boolean)
    .map((t) => new Date(t))
    .filter((d) => !Number.isNaN(d.getTime()));

  if (fillTimes.length > 0) {
    return new Date(
      Math.max(...fillTimes.map((d) => d.getTime())),
    ).toISOString();
  }

  return e?.t ?? null;
}

function eventQtyFromFillsOrEvent(e, fallback = 1) {
  const fills = normalizeFills(e?.fills);
  const fillQty = fills.reduce((acc, f) => acc + Number(f.qty || 0), 0);
  if (fillQty > 0) return fillQty;

  return toFiniteNumber(e?.qty ?? e?.open_qty ?? e?.entry_filled_qty, fallback);
}

function makeTradeKey(strategyId, enterEvent, index) {
  return (
    enterEvent?.trade_key ||
    `${strategyId}-${enterEvent?.entry_order_id || enterEvent?.broker_order_no || index}`
  );
}

/** 현재 시각 기준 '당일 세션(07:00~익일 06:00)'의 head 날짜(YYYY-MM-DD)를 반환 */
function currentSessionYmdKst(now = new Date()) {
  const kst = toKstDate(now);

  // 서머타임인 경우: 07:00 이전이면 '어제 07:00~오늘 06:00'이 현재 세션
  // 비 서머타임인 경우: 08:00 이전이면 '어제 08:00~오늘 07:00'이 현재 세션
  if (IS_DST) {
    if (kst.getHours() < 7) {
      const yesterday = new Date(kst.getTime() - 24 * 3600 * 1000);
      return ymdKst(yesterday);
    }
  } else {
    if (kst.getHours() < 8) {
      const yesterday = new Date(kst.getTime() - 24 * 3600 * 1000);
      return ymdKst(yesterday);
    }
  }

  // 서머타임인 경우: 07:00 이후면 '오늘 07:00~내일 06:00'이 현재 세션
  // 비 서머타임인 경우: 08:00 이후면 '오늘 08:00~내일 07:00'이 현재 세션
  return ymdKst(kst);
}

function fmtKst(isoOrDate) {
  if (!isoOrDate) return '';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  return d.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    // year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** 선택한 '세션 날짜(YYYY-MM-DD)' -> 세션 범위 ISO(+09:00) */
function sessionRangeIso(ymd) {
  if (IS_DST) {
    // 서머타임인 경우
    const startIso = `${ymd}T07:00:00+09:00`;
    const endDay = addDaysKst(ymd, 1);
    const endIso = `${endDay}T06:00:00+09:00`;

    return {
      startIso,
      endIso,
      start: new Date(startIso),
      end: new Date(endIso),
    };
  } else {
    // 비 서머타임인 경우
    const startIso = `${ymd}T08:00:00+09:00`;
    const endDay = addDaysKst(ymd, 1);
    const endIso = `${endDay}T07:00:00+09:00`;

    return {
      startIso,
      endIso,
      start: new Date(startIso),
      end: new Date(endIso),
    };
  }
}

/** 이벤트 히스토리 -> 트레이드 복원 */
function buildTradesFromEvents(strategies) {
  const trades = [];

  for (const s of strategies) {
    const directionLong = s.direction === 'long' || s.direction === true;
    const directionLabel = directionLong ? '매수' : '매도';

    // 중요:
    // events_history는 append 순서를 그대로 사용한다.
    // t 기준 sort 금지. 기존 이벤트의 t가 잘못 저장된 경우 회차 순서가 깨진다.
    const evts = Array.isArray(s.events_history) ? [...s.events_history] : [];

    let open = null;
    let enterIndex = 0;

    for (const e of evts) {
      if (e.type === 'enter') {
        enterIndex += 1;

        const entryFills = normalizeFills(e.fills);
        const entryTime = eventTimeFromFillsOrEvent(e) || e.t;

        const eventDecisionEntryPrice = pickFirstNumber(
          e.decision_entry_price,
          e.real_entry_price,
          e.entry_price,
          e.price,
          decisionEntryPriceFromFills(directionLong, entryFills),
        );

        const eventAvgEntryPrice = pickFirstNumber(
          weightedAvgFromFills(entryFills),
          e.avg_entry_price,
          e.price,
          eventDecisionEntryPrice,
        );

        const qty = eventQtyFromFillsOrEvent(e, 1);
        const targetQty = toFiniteNumber(e.target_qty, qty);

        open = {
          tradeKey: makeTradeKey(s.id, e, enterIndex),
          tradeSeq: e.trade_seq ?? enterIndex,

          t: entryTime,
          price: eventDecisionEntryPrice ?? toFiniteNumber(e.price, 0),

          qty,
          target_qty: targetQty,
          open_qty: qty,
          entry_remaining_qty: toFiniteNumber(e.entry_remaining_qty, 0),

          decision_entry_price: eventDecisionEntryPrice,
          avg_entry_price: eventAvgEntryPrice,

          fills: entryFills,
          broker_order_no: e.broker_order_no ?? null,
          order_status: e.order_status ?? '',
          position_status: e.position_status ?? 'OPEN',
        };
      } else if (e.type === 'exit' && open) {
        const exitFills = normalizeFills(e.fills);

        const avgExitPrice = pickFirstNumber(
          weightedAvgFromFills(exitFills),
          e.avg_exit_price,
          e.price,
        );

        const exitPrice = pickFirstNumber(e.price, avgExitPrice);
        const exitTime = exitTimeFromFillsOrEvent(e);

        const qty = eventQtyFromFillsOrEvent(e, open.open_qty ?? open.qty ?? 1);
        const remainingQty = remainingAfterExit(open, e, qty);

        const profit =
          typeof e.profit === 'number'
            ? e.profit
            : (Number(exitPrice) - Number(open.avg_entry_price ?? open.price)) *
              (directionLong ? 1 : -1) *
              qty;

        const cumPower = e.cum_power != null ? Number(e.cum_power) : null;
        const maxPower = e.max_power != null ? Number(e.max_power) : null;
        const minPower = e.min_power != null ? Number(e.min_power) : null;

        trades.push({
          tradeKey: e.trade_key || open.tradeKey,
          tradeSeq: e.trade_seq ?? open.tradeSeq,

          id: s.id,
          boxline_id: s.boxline_id,
          count_enter: e.trade_seq ?? open.tradeSeq,
          strategyId: s.id,
          directionLabel,
          directionLong,

          qty,
          target_qty: open.target_qty,
          open_qty: remainingQty,
          is_final: e.is_final ?? remainingQty === 0,
          rowKey: `${open.tradeKey}:exit:${e.exit_order_id ?? exitTime}:${exitTime}`,
          entry_remaining_qty: open.entry_remaining_qty,

          position_status:
            e.position_status ?? (remainingQty > 0 ? 'OPEN' : 'CLOSED'),
          broker_order_no: e.broker_order_no ?? open.broker_order_no,
          order_status: open.order_status,

          entry_price: open.price,
          decision_entry_price: open.decision_entry_price ?? open.price,
          avg_entry_price: open.avg_entry_price,
          entry_timestamp: open.t,

          exit_order_id: e.exit_order_id ?? null,
          exit_timestamp: exitTime,
          exit_reason: e.reason ?? '',
          exit_event: e,
          exit_price: exitPrice,
          avg_exit_price: avgExitPrice,

          profit,
          cum_power: cumPower,
          max_power: maxPower,
          min_power: minPower,

          entry_fills: open.fills,
          exit_fills: exitFills,
          live: false,
        });

        open = remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
      }
    }

    // 선택한 세션 범위에 아직 열려 있는 포지션도 표시하고 싶다면 필요.
    // history-range include_open_spanning=true를 사용 중이므로 OPEN trade가 들어올 수 있다.
    if (open && (s.is_enter || s.position_status === 'OPEN')) {
      trades.push({
        tradeKey: open.tradeKey,
        tradeSeq: open.tradeSeq,

        id: s.id,
        boxline_id: s.boxline_id,
        count_enter: open.tradeSeq,
        strategyId: s.id,
        directionLabel,
        directionLong,

        qty: toFiniteNumber(s.open_qty ?? open.open_qty ?? open.qty, open.qty),
        target_qty: toFiniteNumber(
          s.target_qty ?? open.target_qty,
          open.target_qty,
        ),
        open_qty: toFiniteNumber(
          s.open_qty ?? open.open_qty ?? open.qty,
          open.qty,
        ),
        entry_remaining_qty: toFiniteNumber(
          s.entry_remaining_qty ?? open.entry_remaining_qty,
          0,
        ),

        position_status: s.position_status ?? open.position_status ?? 'OPEN',
        broker_order_no: open.broker_order_no,
        order_status: open.order_status,

        entry_price: open.price,
        decision_entry_price: open.decision_entry_price ?? open.price,
        avg_entry_price: open.avg_entry_price,
        entry_timestamp: open.t,

        exit_order_id: null,
        exit_timestamp: null,
        exit_reason: '',
        exit_price: null,
        avg_exit_price: null,

        profit: 0,
        cum_power: null,
        max_power: null,
        min_power: null,

        entry_fills: open.fills,
        exit_fills: [],
        live: true,
      });
    }
  }

  return sortTradesByExitTime(trades);
}

export default function AutoTradingDetailHistoryPage() {
  const API = process.env.REACT_APP_API_URL;

  const [sessionYmd, setSessionYmd] = useState(currentSessionYmdKst());
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHist, setLoadingHist] = useState(false);
  const inFlight = useRef(null);

  // 범위 조회
  async function fetchHistoryRange(startIso, endIso) {
    if (inFlight.current) {
      try {
        inFlight.current.abort?.();
      } catch {}
      inFlight.current = null;
    }
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    setLoadingHist(true);
    try {
      const res = await axios.get(
        `${API}/auto-trading/strategies/history-range`,
        {
          params: {
            start: startIso,
            end: endIso,
            include_open_spanning: true,
          },
          signal: ctrl.signal, // 취소 신호 전달
        },
      );
      return res.data ?? [];
    } catch (e) {
      if (!isCanceled(e)) throw e;
      return null;
    } finally {
      setLoadingHist(false);
      inFlight.current = null;
    }
  }

  const session = useMemo(() => sessionRangeIso(sessionYmd), [sessionYmd]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { startIso, endIso } = session;
        const h = await fetchHistoryRange(startIso, endIso);
        if (mounted && h) setHistory(h);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      if (inFlight.current) {
        try {
          inFlight.current.abort?.();
        } catch {}
      }
    };
  }, [sessionYmd]); // 세션 변경 시 재조회

  const tradesAll = useMemo(() => buildTradesFromEvents(history), [history]);

  const tradesInSession = useMemo(() => {
    const { start, end } = session;
    const inRange = (ts) => {
      const t = new Date(ts);
      return t >= start && t <= end;
    };
    // 진입 또는 청산이 세션 범위에 걸치면 포함
    return tradesAll.filter(
      (t) =>
        inRange(t.entry_timestamp) ||
        (t.exit_timestamp && inRange(t.exit_timestamp)),
    );
  }, [tradesAll, session]);

  // 요약 통계
  const summary = useMemo(() => {
    const realizedLegs = tradesInSession.filter(
      (t) => !t.live && t.exit_timestamp,
    );
    const closed = closedTradeCycles(tradesInSession);
    const n = closed.length;
    const profitArr = closed.map((t) => Number(t.profit) || 0);
    const sum = realizedLegs.reduce((a, t) => a + (Number(t.profit) || 0), 0);
    // break_even(본절 청산)은 무승부(draw)로 집계하고 승/패/승률에서 제외
    const draws = closed.filter(
      (t) => isBreakEvenExit(t.exit_reason) && !t.hasPartialExits,
    ).length;
    const nonDraw = closed.filter(
      (t) => !(isBreakEvenExit(t.exit_reason) && !t.hasPartialExits),
    );
    const wins = nonDraw.filter((t) => (Number(t.profit) || 0) > 0).length;
    const losses = nonDraw.filter((t) => (Number(t.profit) || 0) < 0).length;
    const denom = wins + losses;
    const wr = denom ? (wins / denom) * 100 : 0;
    const avg = n ? profitArr.reduce((a, b) => a + b, 0) / n : 0;
    const maxP = n ? Math.max(...profitArr) : 0;
    const minP = n ? Math.min(...profitArr) : 0;

    // 평균 보유시간(청산 기록 있는 트레이드만)
    const holdTimes = closed.map(
      (t) => new Date(t.exit_timestamp) - new Date(t.entry_timestamp),
    );
    const avgHoldMs = holdTimes.length
      ? Math.round(holdTimes.reduce((a, b) => a + b, 0) / holdTimes.length)
      : 0;
    const toHMS = (ms) => {
      const s = Math.floor(ms / 1000);
      const hh = Math.floor(s / 3600);
      const mm = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    };

    return {
      count: n,
      wins,
      draws,
      losses,
      winRate: wr,
      net: sum,
      avg,
      max: maxP,
      min: minP,
      avgHold: toHMS(avgHoldMs),
    };
  }, [tradesInSession]);

  const onPrev = () => setSessionYmd((d) => addDaysKst(d, -1));
  const onNext = () => {
    setSessionYmd((d) => {
      const max = currentSessionYmdKst(); // 지금 시각 기준 '당일 세션'
      const next = addDaysKst(d, 1);
      return next > max ? d : next;
    });
  };
  const refresh = async () => {
    try {
      const { startIso, endIso } = session; // 서머타임: KST 07:00 ~ 익일 06:00, 비 서머타임: KST 08:00 ~ 익일 07:00
      const h = await fetchHistoryRange(startIso, endIso);
      if (h) setHistory(h);
    } catch (e) {
      if (!isCanceled(e)) console.error(e);
    }
  };

  return (
    <div className='px-6 max-w-fit mx-auto'>
      <div className='flex flex-wrap items-end gap-3 justify-between'>
        <h1 className='text-xl font-bold'>일별 거래내역 상세</h1>
        <div className='flex items-center gap-2'>
          <button
            onClick={onPrev}
            className='rounded border px-3 py-1 hover:bg-gray-50'
          >
            ◀
          </button>
          <input
            type='date'
            className='rounded border px-2 py-1'
            value={sessionYmd}
            max={currentSessionYmdKst()} // 미래 세션 선택 방지
            onChange={(e) => {
              const v = e.target.value;
              const max = currentSessionYmdKst();
              setSessionYmd(v > max ? max : v); // 초과 시 현재 세션으로 클램프
            }}
          />
          <button
            onClick={onNext}
            className='rounded border px-3 py-1 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
            disabled={sessionYmd >= currentSessionYmdKst()} // 현재 세션이면 비활성
          >
            ▶
          </button>
          <button
            onClick={refresh}
            className='rounded border px-3 py-1 hover:bg-gray-50'
            disabled={loadingHist}
          >
            {loadingHist ? '갱신중…' : '히스토리 새로고침'}
          </button>
        </div>
      </div>

      {/* 세션 범위 표시 */}
      <p className='text-sm text-gray-600 mt-1'>
        세션:{' '}
        <span className='font-medium'>
          {sessionYmd} {IS_DST ? '07:00:00' : '08:00:00'}
        </span>{' '}
        ~{' '}
        <span className='font-medium'>
          {addDaysKst(sessionYmd, 1)} {IS_DST ? '06:00:00' : '07:00:00'}
        </span>{' '}
        (KST)
      </p>

      {/* 요약 카드 */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mt-4'>
        <div className='rounded-lg border p-3'>
          <div className='text-gray-600 text-sm'>총 진입 횟수</div>
          <div className='text-xl font-semibold'>{summary.count}</div>
        </div>
        <div className='rounded-lg border p-3'>
          <div className='text-gray-600 text-sm'>승/무/패 (승률)</div>
          <div className='text-xl font-semibold'>
            {summary.wins} / {summary.draws} / {summary.losses}{' '}
            <span className='text-gray-500 text-base'>
              ({summary.winRate.toFixed(1)}%)
            </span>
          </div>
        </div>
        <div className='rounded-lg border p-3'>
          <div className='text-gray-600 text-sm'>일 누적 순손익(포인트)</div>
          <div
            className={`text-xl font-semibold ${summary.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}
          >
            {summary.net.toFixed(2)}
          </div>
        </div>
        <div className='rounded-lg border p-3'>
          <div className='text-gray-600 text-sm'>평균/최대/최소(포인트)</div>
          <div className='text-xl font-semibold'>
            {summary.avg.toFixed(2)}{' '}
            <span className='text-gray-500 text-base'>
              (최대 {summary.max.toFixed(2)}, 최소 {summary.min.toFixed(2)})
            </span>
          </div>
        </div>
        <div className='rounded-lg border p-3 col-span-2 md:col-span-4'>
          <div className='text-gray-600 text-sm'>평균 보유시간</div>
          <div className='text-xl font-semibold'>{summary.avgHold}</div>
        </div>
      </div>

      {/* 상세 테이블 */}
      <div className='overflow-x-auto rounded-lg border border-gray-200 mt-6'>
        <table className='min-w-full text-sm'>
          <thead className='bg-gray-50 border-b'>
            <tr>
              <th className='px-2 py-2 text-center'>매수/매도</th>
              <th className='px-2 py-2 text-center'>진입 횟수</th>
              <th className='px-2 py-2 text-center'>수량</th>
              <th className='px-2 py-2 text-center'>대표 진입가</th>
              <th className='px-2 py-2 text-center'>평균 진입가</th>
              <th className='px-2 py-2 text-center'>진입 시점</th>
              <th className='px-2 py-2 text-center'>평균 청산가</th>
              <th className='px-2 py-2 text-center'>청산 시점</th>
              <th className='px-2 py-2 text-center'>누적 강도</th>
              <th className='px-2 py-2 text-center'>최대 강도</th>
              <th className='px-2 py-2 text-center'>최저 강도</th>
              <th className='px-2 py-2 text-center'>상태</th>
              <th className='px-2 py-2 text-center'>청산 사유</th>
              <th className='px-2 py-2 text-center'>실제 총손익(포인트)</th>
              <th className='px-2 py-2 text-center'>자동거래/박스라인 ID</th>
            </tr>
          </thead>
          <tbody>
            {(loading && (
              <tr>
                <td className='px-2 py-3 text-gray-400' colSpan={15}>
                  로딩 중…
                </td>
              </tr>
            )) ||
              (tradesInSession.length === 0 && (
                <tr>
                  <td className='px-2 py-3 text-gray-400' colSpan={15}>
                    해당 날짜의 거래 내역이 없습니다.
                  </td>
                </tr>
              )) ||
              tradesInSession.map((t, idx) => (
                <tr
                  key={
                    t.rowKey ||
                    t.tradeKey ||
                    `${t.strategyId}-${t.tradeSeq || idx}-${t.entry_timestamp}-${t.exit_timestamp || 'live'}`
                  }
                  className={idx % 2 ? 'bg-gray-50' : 'bg-white'}
                >
                  <td className='px-2 py-2 text-center'>{t.directionLabel}</td>

                  <td className='px-2 py-2 text-center'>
                    {t.count_enter ?? 0}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {fmtQty(t.qty)} / {fmtQty(t.target_qty)}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {fmtPrice(t.decision_entry_price ?? t.entry_price)}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {fmtPrice(t.avg_entry_price)}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {fmtKst(t.entry_timestamp)}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {fmtPrice(t.avg_exit_price ?? t.exit_price)}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.exit_timestamp ? fmtKst(t.exit_timestamp) : ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.cum_power != null ? t.cum_power.toFixed(0) : ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.max_power != null ? t.max_power.toFixed(0) : ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.min_power != null ? t.min_power.toFixed(0) : ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.position_status || ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.live
                      ? 'LIVE'
                      : mapExitReason(t.exit_reason, t.exit_event)}
                  </td>

                  <td
                    className={`px-2 py-2 text-center ${
                      t.profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {t.live
                      ? '—'
                      : t.profit != null
                        ? Number(t.profit).toFixed(2)
                        : ''}
                  </td>

                  <td className='px-2 py-2 text-center'>
                    {t.id} / {t.boxline_id ?? ''}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
